import React from 'react';

/**
 * Interface representing a single question's result
 */
export interface ListeningResult {
    taskId: string;
    taskNumber: number;
    taskType: string;
    userResponse: string;
    correctAnswer: string;
    score: number;
    maxScore: number;
    traits?: Record<string, { score: number; maxScore: number; comments?: string }>;
}

interface PteCoreListeningScoreProps {
    results: ListeningResult[];
    onRetry: () => void;
    onNext?: () => void;
    nextLabel?: string;
}

const TraitBar: React.FC<{ label: string; score: number; max: number; comments?: string }> = ({ label, score, max, comments }) => {
    const percentage = (score / max) * 100;
    const barColor = percentage >= 80 ? 'bg-green-500' : percentage >= 50 ? 'bg-amber-500' : 'bg-red-500';

    return (
        <div className="mb-4 last:mb-0">
            <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-bold text-gray-700 capitalize">{label.replace(/([A-Z])/g, ' $1')}</span>
                <span className="text-xs font-black text-gray-500">{score} / {max}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden border border-gray-200 shadow-inner">
                <div
                    className={`h-full transition-all duration-1000 ease-out ${barColor}`}
                    style={{ width: `${percentage}%` }}
                ></div>
            </div>
            {comments && <p className="text-[10px] text-gray-400 mt-1 italic leading-tight">{comments}</p>}
        </div>
    );
};

// CLB levels for PTE Core Listening
const getListeningCLB = (score: number): number => {
    if (score >= 89) return 10;
    if (score >= 82) return 9;
    if (score >= 71) return 8;
    if (score >= 60) return 7;
    if (score >= 50) return 6;
    if (score >= 39) return 5;
    if (score >= 28) return 4;
    if (score >= 18) return 3;
    return 3; // Below 18
};

const PteCoreListeningScore: React.FC<PteCoreListeningScoreProps> = ({ results, onRetry, onNext, nextLabel }) => {
    const totalScore = results.reduce((sum, r) => sum + r.score, 0);
    const totalMaxScore = results.reduce((sum, r) => sum + r.maxScore, 0);

    // Normalize to 90
    const scoreOut90 = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 90) : 0;
    const clbLevel = getListeningCLB(scoreOut90);

    return (
        <div className="ptest-root bg-gray-50 min-h-screen p-4 md:p-8">
            {/* Overall Score Header remains same */}
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden mb-8 border border-gray-100">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8 text-center text-white">
                    <h2 className="text-3xl font-extrabold mb-2">Listening Test Results</h2>
                    <p className="text-blue-100 opacity-90">Great job on completing your PTE Core Listening practice!</p>
                </div>

                <div className="p-8">
                    <div className="flex flex-wrap justify-center gap-6 md:gap-12">
                        {/* Score out of 90 */}
                        <div className="text-center">
                            <span className="block text-xs text-gray-500 font-bold uppercase tracking-widest mb-2">Overall Score</span>
                            <div className="inline-flex items-baseline gap-1">
                                <span className="text-6xl font-black text-gray-900">{scoreOut90}</span>
                                <span className="text-2xl font-bold text-gray-400">/ 90</span>
                            </div>
                        </div>

                        {/* CLB Level */}
                        <div className="text-center">
                            <span className="block text-xs text-gray-500 font-bold uppercase tracking-widest mb-2">CLB Level</span>
                            <div className="bg-amber-100 text-amber-700 px-6 py-2 rounded-2xl border border-amber-200">
                                <span className="text-5xl font-black">{clbLevel}</span>
                            </div>
                        </div>
                    </div>

                    {/* CLB Reference Table */}
                    <div className="mt-10 overflow-x-auto">
                        <h4 className="text-center text-sm font-bold text-gray-700 mb-4">CLB - PTE Core Listening Conversion</h4>
                        <table className="mx-auto text-xs border-collapse w-full max-w-md shadow-sm rounded-lg overflow-hidden">
                            <thead>
                                <tr className="bg-gray-100 text-gray-600">
                                    <th className="px-4 py-3 border-b text-center font-bold">CLB Level</th>
                                    <th className="px-4 py-3 border-b text-center font-bold">PTE Score Range</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { clb: 10, range: '89–90' },
                                    { clb: 9, range: '82–88' },
                                    { clb: 8, range: '71–81' },
                                    { clb: 7, range: '60–70' },
                                    { clb: 6, range: '50–59' },
                                    { clb: 5, range: '39–49' },
                                    { clb: 4, range: '28–38' },
                                    { clb: 3, range: '18–27' },
                                ].map((row) => (
                                    <tr
                                        key={row.clb}
                                        className={clbLevel === row.clb ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-600'}
                                    >
                                        <td className="px-4 py-2 border-b text-center">{row.clb}</td>
                                        <td className="px-4 py-2 border-b text-center">{row.range}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Detailed Question Feedback */}
            <div className="max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <span>Detailed Feedback</span>
                    <div className="h-px flex-1 bg-gray-200"></div>
                </h3>

                {results.map((result, index) => (
                    <div key={`${result.taskId}-${index}`} className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden transition-all hover:shadow-md">
                        <div className="bg-gray-50 px-6 py-4 flex flex-wrap justify-between items-center border-b gap-4">
                            <div className="flex items-center gap-3">
                                <span className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                                    {index + 1}
                                </span>
                                <h4 className="font-bold text-gray-800 text-lg">
                                    {result.taskType}
                                </h4>
                            </div>
                            <div className={`px-4 py-1.5 rounded-full text-sm font-bold flex items-center gap-2 ${result.score === result.maxScore ? 'bg-green-100 text-green-700' :
                                result.score > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                                }`}>
                                Score: {result.score} / {result.maxScore}
                                {result.score === result.maxScore && <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>}
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* User Response */}
                                <div>
                                    <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Your Response</h5>
                                    <div className={`p-4 rounded-xl text-sm border min-h-[80px] ${result.score === result.maxScore ? 'bg-green-50 border-green-100 text-green-800' :
                                        result.score > 0 ? 'bg-amber-50 border-amber-100 text-amber-800' : 'bg-red-50 border-red-100 text-red-800'
                                        }`}>
                                        {result.userResponse || <span className="italic opacity-60">(No response provided)</span>}
                                    </div>

                                    {/* Trait breakdown for SST tasks */}
                                    {result.traits && (
                                        <div className="mt-6 p-5 bg-gray-50 rounded-xl border border-gray-200">
                                            <h6 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Trait-Based Breakdown</h6>
                                            {Object.entries(result.traits).map(([key, trait]) => (
                                                <TraitBar
                                                    key={key}
                                                    label={key}
                                                    score={trait.score}
                                                    max={trait.maxScore}
                                                    comments={trait.comments}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Correct Answer */}
                                <div>
                                    <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Correct Answer / Insight</h5>
                                    <div className="p-4 rounded-xl text-sm bg-gray-50 border border-gray-200 text-gray-700 min-h-[80px]">
                                        {result.correctAnswer || <span className="italic opacity-60 text-gray-400">Not available</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Action Buttons */}
            <div className="max-w-4xl mx-auto flex justify-between items-center mt-12 mb-20 bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                <button
                    onClick={onRetry}
                    className="px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-95"
                >
                    Retry Practice
                </button>
                {onNext && (
                    <button
                        onClick={onNext}
                        className="px-10 py-3 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95"
                    >
                        {nextLabel || 'Next Section'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default PteCoreListeningScore;
